import FiltersBadgeGroup from "./FiltersBadgeGroup";
import styles from "./PropertyFiltersSection.module.scss";
import BottomSheetOverlay from "@Components/BottomSheetOverlay";
import FullPageLoader from "@Components/FullPageLoader";
import Portal from "@Components/Portal";
import {
  applyPropertyFilter,
  updatePropertyFilterOptions,
} from "@Pages/PropertyPage/data/propertyPageReducer";
import adaptPropertyFilterOptions from "@Pages/PropertyPage/data/adaptPropertyFilterOptions";
import fetchPropertyFilterOptions from "@Pages/PropertyPage/data/fetchPropertyFilterOptions";
import BottomOverlayContainer from "@Pages/Search/components/FilterSection/components/BottomOverlayContainer/BottomOverlayContainer";
import filtersModalReducer from "@Pages/Search/components/FilterSection/components/FiltersModal/filtersModalReducer";
import { PROPERTY_FILTERS } from "@Pages/Search/components/FilterSection/data/filtersList";
import { ActiveFilterType } from "@Pages/Search/components/FilterSection/types";
import getAllFilterCount from "@Pages/Search/utils/getAllFilterCount";
import getFilterAction from "@Pages/Search/utils/getFilterAction";
import getFilterActiveStatus from "@Pages/Search/utils/getFilterActiveStatus";
import updateFilterValueByKey from "@Pages/Search/utils/updateFilterValueByKey";
import { AppState } from "@Store/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { COUNTRIES_WITH_MONTHLY_DURATION, PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { Suspense, lazy, useEffect, useMemo, useReducer, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import TabSection from "./RoomTypeTabSection";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS, APP_PAGE, CLICKED_ITEM, FILTER_FLOW } from "@Utils/webApputils/analyticEvents";
//

const ActiveFilterComponent = lazy(
  () =>
    import(
      "@Pages/Search/components/FilterSection/components/ActiveFilterComponent/ActiveFilterComponent"
    ),
);

const PropertyFiltersSection = ({ tabdata, setActiveTab, activeTab, isDesktop = false }: any) => {
  const {
    filters,
    propertyFilterOptions,
    inventoryId,
    countryCode,
    inventoryTags,
    roomTypesData,
    inventoryDuration,
  } = useSelector(
    ({ propertyPageData }: AppState) => ({
      filters: propertyPageData.filters,
      propertyFilterOptions: propertyPageData.propertyFilterOptions,
      inventoryId: propertyPageData.id,
      countryCode: propertyPageData.data?.location?.country?.short_name,
      inventoryTags: propertyPageData.data?.tags,
      roomTypesData: propertyPageData.roomTypes.data,
      inventoryDuration: propertyPageData.data?.pricing?.duration,
    }),
    shallowEqual,
  );
  const isRoomTypeV2 = true;
  const { isAbove } = useResponsiveness();
  const isAboveMD = isAbove(SCREEN_SIZE.MD);
  const hasDualOccupancy = useMemo(() => {
    if (inventoryTags?.includes("dual_occupancy")) return true;
    if (Array.isArray(roomTypesData)) {
      return roomTypesData.some((room: any) => room?.tags?.includes("dual_occupancy"));
    }
    return false;
  }, [inventoryTags, roomTypesData]);

  const adaptedFilterCount = useMemo(() => {
    const adapted = adaptPropertyFilterOptions(propertyFilterOptions);
    // Tag present → show the pill and keep Yes selectable. DualOccupancy reads
    // `available` to render the panel and `true` for the option count; the
    // filter-count API shape ({ available, count }) would grey Yes out.
    const dualOccupancyFromTag = { available: true, true: 1 };
    if (hasDualOccupancy && adapted) {
      return {
        ...adapted,
        data: {
          ...adapted.data,
          dual_occupancy: dualOccupancyFromTag,
        },
      };
    }
    if (hasDualOccupancy && !adapted) {
      return {
        meta: { count: 1 },
        data: {
          dual_occupancy: dualOccupancyFromTag,
        },
      };
    }
    return adapted;
  }, [propertyFilterOptions, hasDualOccupancy]);

  const propertyPageDispatch = useDispatch();

  // Fetch dynamic filter options on client side when inventory ID is available
  useEffect(() => {
    if (inventoryId && !propertyFilterOptions) {
      const isMonthlyCountry = COUNTRIES_WITH_MONTHLY_DURATION.includes(countryCode);
      let duration: "weekly" | "monthly" = isMonthlyCountry ? "monthly" : "weekly";
      if (["weekly", "monthly"].includes(inventoryDuration)) {
        duration = inventoryDuration;
      }
      fetchPropertyFilterOptions(inventoryId, duration).then((options) => {
        if (options) {
          propertyPageDispatch(updatePropertyFilterOptions(options));
        }
      });
    }
  }, [inventoryId]);
  const [activeFilter, setActiveFilter] = useState<ActiveFilterType>("");
  const [currFilters, dispatch] = useReducer(filtersModalReducer, filters);
  const clearDisable = getAllFilterCount(currFilters) === 0;
  const filterCount = getAllFilterCount(filters);

  useEffect(() => {
    dispatch({ type: "applyAll", payload: filters });
  }, [JSON.stringify(filters)]);

  const hideBottomSheetOverlay = () => {
    setActiveFilter("");
  };

  const hideBottomSheetOverlayWithFilterApply = () => {
    dispatch({ type: "applyAll", payload: filters });
    hideBottomSheetOverlay();
  };

  const handleFilterLabelClick = (name: ActiveFilterType = "") => {
    setActiveFilter(name);
    const gaEvent = getFilterAction(name);
    logAppEvent({
      eventName: APP_EVENTS.FILTER_CTA_CLICKED,
      payload: {
        filter_name: name,
        filter_flow: FILTER_FLOW.QUICK_FILTER,
        screen_name: APP_PAGE.PROPERTY,
      },
    });
    gtagEventEmitter({ action: gaEvent, category: PAGE.INVENTORY });
  };

  const handleFilterClose = () => {
    setActiveFilter("");
  };

  const handleApplyFilters = (newFilters: any = {}) => {
    const filtersToApply =
      isDesktop && isAboveMD
        ? {
            ...currFilters,
            ...newFilters,
          }
        : { ...currFilters };

    if (filtersToApply.minLeaseDuration === "all_selected") {
      delete filtersToApply.minLeaseDuration;
      delete filtersToApply.maxLeaseDuration;
    }

    propertyPageDispatch(applyPropertyFilter(filtersToApply));
    if (!isDesktop || !isAboveMD) hideBottomSheetOverlay();
  };

  useEffect(() => {
    if (isDesktop && JSON.stringify(currFilters) !== JSON.stringify(filters)) {
      propertyPageDispatch(applyPropertyFilter(currFilters));
    }
  }, [currFilters]);

  const handleOnClearFilter = () => {
    if (!clearDisable) {
      dispatch({ type: "reset", payload: {} });
      propertyPageDispatch(applyPropertyFilter({}));
      logAppEvent({
        eventName: APP_EVENTS.CTA_CLICKED,
        payload: {
          cta_name: CLICKED_ITEM.CLEAR_FILTER,
          screen_name: APP_PAGE.PROPERTY,
          section: "room_type_section",
        },
      });
      gtagEventEmitter({
        action: "clear_filter_clicked",
      });
    }
  };

  const handleClearClick = (filterName?: string) => {
    const filter = filterName
      ? PROPERTY_FILTERS.find((f: any) => f.name === filterName || f.title === filterName)
      : PROPERTY_FILTERS.find((f: any) => f.name === activeFilter);
    const key = filter?.key;

    if (key) {
      const updatedState = updateFilterValueByKey(key, "", currFilters);
      dispatch({ type: "applyAll", payload: updatedState });
      propertyPageDispatch(applyPropertyFilter(updatedState));
    }

    hideBottomSheetOverlay();
  };

  useEffect(() => {
    window.addEventListener("clearFilter", handleOnClearFilter);
    return () => {
      window.removeEventListener("clearFilter", handleOnClearFilter);
    };
  }, []);

  const { t } = useTranslationHook();

  return (
    <>
      <div
        className={formatClassNames(
          styles.container,
          isRoomTypeV2 && styles.containerV2,
          isDesktop && styles.desktop,
        )}
      >
        <div className={styles.filterList}>
          <FiltersBadgeGroup
            filters={filters}
            onFilterClick={handleFilterLabelClick}
            onFilterClose={handleFilterClose}
            isDesktop={isDesktop && isAboveMD}
            onClearClick={handleClearClick}
            filtersState={currFilters}
            activeFilter={activeFilter}
            dispatch={dispatch}
            onApplyClick={handleApplyFilters}
            dynamiceFilterCount={adaptedFilterCount}
            hasDualOccupancy={hasDualOccupancy}
          />
          <span
            data-testid="property-room-type-filter-clear-button"
            role="button"
            tabIndex={0}
            className={formatClassNames(styles.clearBtn, clearDisable && styles.disable)}
            onClick={handleOnClearFilter}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleOnClearFilter();
              }
            }}
          >
            {t("propertypage.roomtypesectiondynamic.propertyfilterssection.clear")}
          </span>
        </div>
        <div>
          {/* Bottom Overlays */}
          {activeFilter !== "" && (!isDesktop || !isAboveMD) && (
            <Portal rootId="forPortal">
              <BottomSheetOverlay
                hideBottomSheetOverlay={hideBottomSheetOverlayWithFilterApply}
                isHistoryPush={!isDesktop}
                name={`RoomTypeFilter-${activeFilter}`}
              >
                <Suspense fallback={<FullPageLoader height="30vh" />}>
                  <BottomOverlayContainer
                    isClearActive={getFilterActiveStatus(activeFilter, currFilters, true, true)}
                    onApplyClick={handleApplyFilters}
                    onClearClick={() => handleClearClick(activeFilter)}
                    hideLine
                  >
                    <ActiveFilterComponent
                      filterName={activeFilter}
                      filtersState={currFilters}
                      dispatch={dispatch}
                      universityData={null}
                      localityData={null}
                      hideBottomSheetOverlay={hideBottomSheetOverlayWithFilterApply}
                      withCross
                      openingSource="roomTypeFilterMobile"
                      dynamiceFilterCount={adaptedFilterCount}
                      restrictToAvailable
                    />
                  </BottomOverlayContainer>
                </Suspense>
              </BottomSheetOverlay>
            </Portal>
          )}
        </div>
      </div>
      <TabSection
        tabdata={tabdata}
        setActiveTab={setActiveTab}
        activeTab={activeTab}
        appliedFilters={filters}
        isDesktop={isDesktop}
        filterCount={filterCount}
      />
    </>
  );
};

export default wrapperHOC(PropertyFiltersSection, {
  componentName: "PropertyFiltersSection-RoomTypeSection-PropertyPage",
  showForChina: true,
});
